import { useCallback, type FormEvent } from 'react'
import {
  useCreateAgencyUserMutation,
  useUpdateAgencyUserMutation,
} from '@/modules/agencies/api/agenciesApi'
import type { AgencyUser } from '@/modules/agencies/types/agencyUser'
import type { AgencyUserFormField } from '@/modules/agencies/types/agencyUserForm'
import {
  validateAgencyUserCreate,
  validateAgencyUserUpdate,
} from '@/modules/agencies/utils/validateAgencyUserForm'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import {
  selectAgencyUserFormAgencyId,
  selectAgencyUserFormFieldErrors,
  selectAgencyUserFormFields,
  selectAgencyUserFormMode,
  selectAgencyUserFormSubmitError,
  selectEditingAgencyUserId,
  selectIsAgencyUserFormOpen,
  selectIsAgencyUserFormSubmitting,
} from '@/store/selectors/agencyUsersUiSelectors'
import {
  agencyUserFormClosed,
  agencyUserFormFailed,
  agencyUserFormFieldChanged,
  agencyUserFormSubmitted,
  agencyUserFormSucceeded,
  agencyUserFormValidationFailed,
  createAgencyUserOpened,
  editAgencyUserOpened,
} from '@/store/slices/agencyUsersUiSlice'
import { getAuthErrorMessage } from '@/utils/getAuthErrorMessage'

export function useAgencyUserForm() {
  const dispatch = useAppDispatch()
  const mode = useAppSelector(selectAgencyUserFormMode)
  const isOpen = useAppSelector(selectIsAgencyUserFormOpen)
  const agencyId = useAppSelector(selectAgencyUserFormAgencyId)
  const editingUserId = useAppSelector(selectEditingAgencyUserId)
  const fields = useAppSelector(selectAgencyUserFormFields)
  const fieldErrors = useAppSelector(selectAgencyUserFormFieldErrors)
  const submitError = useAppSelector(selectAgencyUserFormSubmitError)
  const isSubmitting = useAppSelector(selectIsAgencyUserFormSubmitting)
  const [createAgencyUser] = useCreateAgencyUserMutation()
  const [updateAgencyUser] = useUpdateAgencyUserMutation()

  const openCreate = useCallback(
    (nextAgencyId: string) => {
      dispatch(createAgencyUserOpened({ agencyId: nextAgencyId }))
    },
    [dispatch],
  )

  const openEdit = useCallback(
    (nextAgencyId: string, user: AgencyUser) => {
      dispatch(
        editAgencyUserOpened({
          agencyId: nextAgencyId,
          userId: user.id,
          firstName: user.firstName,
          lastName: user.lastName,
          email: user.email,
          role: user.role,
          status: user.status,
        }),
      )
    },
    [dispatch],
  )

  const closeForm = useCallback(() => {
    if (isSubmitting) {
      return
    }
    dispatch(agencyUserFormClosed())
  }, [dispatch, isSubmitting])

  const onFieldChange = useCallback(
    (field: AgencyUserFormField, value: string) => {
      dispatch(agencyUserFormFieldChanged({ field, value }))
    },
    [dispatch],
  )

  const onSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault()

      if (!agencyId) {
        dispatch(agencyUserFormFailed('Missing agency id.'))
        return
      }

      if (mode === 'create') {
        const validation = validateAgencyUserCreate(fields)
        if (!validation.ok) {
          dispatch(agencyUserFormValidationFailed(validation.fieldErrors))
          return
        }

        dispatch(agencyUserFormSubmitted())
        const result = await createAgencyUser({
          agencyId,
          body: validation.value,
        })

        if (result.error) {
          dispatch(agencyUserFormFailed(getAuthErrorMessage(result.error)))
          return
        }

        dispatch(agencyUserFormSucceeded())
        return
      }

      if (mode === 'edit') {
        if (!editingUserId) {
          dispatch(agencyUserFormFailed('Missing user id.'))
          return
        }

        const validation = validateAgencyUserUpdate(fields)
        if (!validation.ok) {
          dispatch(agencyUserFormValidationFailed(validation.fieldErrors))
          return
        }

        dispatch(agencyUserFormSubmitted())
        const result = await updateAgencyUser({
          agencyId,
          userId: editingUserId,
          body: validation.value,
        })

        if (result.error) {
          dispatch(agencyUserFormFailed(getAuthErrorMessage(result.error)))
          return
        }

        dispatch(agencyUserFormSucceeded())
      }
    },
    [
      agencyId,
      createAgencyUser,
      dispatch,
      editingUserId,
      fields,
      mode,
      updateAgencyUser,
    ],
  )

  return {
    mode,
    isOpen,
    isCreateMode: mode === 'create',
    isEditMode: mode === 'edit',
    fields,
    fieldErrors,
    submitError,
    isSubmitting,
    openCreate,
    openEdit,
    closeForm,
    onFieldChange,
    onSubmit,
  }
}
